import { ChevronRight } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { useAuth } from "@/lib/auth";
import {
  ACCOUNT_ITEM, hasSettings, visibleSettingsSections, type NavItem, type NavSection,
} from "@/lib/nav";
import { cn } from "@/lib/utils";

/**
 * Settings: every page that is set up once and changed now and then, one click down from the foot
 * of the sidebar, so the sidebar keeps to the pages people work in. `lib/nav.ts` says which page is
 * which; this draws a card for each, under the heading it had in the sidebar — tap-ucaas's section
 * landing, one level deep. The pages keep their own paths, so a link or a bookmark to one still
 * opens it.
 */
export function SettingsHubPage() {
  const { user } = useAuth();
  if (!user) return null;
  // A rep has nothing here but their own account, and a page of one card is a detour to it.
  if (!hasSettings(user.role)) return <Navigate to={ACCOUNT_ITEM.to} replace />;

  const sections: NavSection[] = [
    ...visibleSettingsSections(user.role),
    { heading: "Your account", items: [ACCOUNT_ITEM] },
  ];

  return (
    <Page>
      <PageHeader
        title="Settings"
        description="The pages you set up once and change now and then. The ones you work in every day are in the sidebar."
      />
      <div className="space-y-6">
        {sections.map((section) => {
          const id = `settings-${section.heading.toLowerCase().replace(/\W+/g, "-")}`;
          return (
            <section key={section.heading} aria-labelledby={id}>
              <h2
                id={id}
                className="pb-2 text-2xs font-medium uppercase tracking-wide text-text-subtle"
              >
                {section.heading}
              </h2>
              <ul className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-3">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <SettingsCard item={item} />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </Page>
  );
}

function SettingsCard({ item }: { item: NavItem }) {
  return (
    <Link
      to={item.to}
      className={cn(
        "group flex h-full items-start gap-3 rounded-lg border border-border bg-surface p-3.5 shadow-xs",
        "transition-colors hover:border-border-strong hover:bg-surface-hover",
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-surface-sunken text-text-muted group-hover:text-text">
        <item.icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-text">{item.label}</span>
        {item.description && (
          <span className="mt-0.5 block text-xs leading-relaxed text-text-muted">{item.description}</span>
        )}
      </span>
      <ChevronRight
        className="mt-0.5 size-4 shrink-0 text-text-subtle transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </Link>
  );
}
